import { pluralize } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';

import { usePhotoUriForLayoutSize, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { ScanPartContext, scanPartHasDuplicatePhotos, ScanPartOptimized, ScanPhoto } from '@kaiizen/kai-scan-template-system';
import React, { useRef, useState } from 'react';

import { useLazyInView } from '@/Client/Lib';
import { useBulkPhotoUploader } from '@/lib/photoLib';
import { CancelIcon, UploadIndicatorIcon } from '../../Icons';
import { useNavigateToPhoto } from '../../State/kaiNavigation';
import { COLORS2, LAYOUT2 } from '../../Styles';
import { TYPOGRAPHY2 } from '../../Styles/TYPOGRAPHY2';
import { Button } from '../../Widgets';
import { PhotoDropZone } from '../../Widgets/PhotoDropZone';
import { AddPhotoIcon } from '../../Widgets/StyledIcons';

export type PhotoGallerySection = {
  header: React.ReactNode;
  scanPart: ScanPartOptimized;
  scanPartContext: ScanPartContext;
  photos: ScanPhoto[];
  onAddPhotos?: (photos: ScanPhoto[]) => void;
  onRemoveDuplicates?: () => void;
};

const THUMBNAIL_SIZE = 120;

const SECTION_STYLE = css`
  label: PHOTO_GALLERY_SECTION_STYLE;
  padding-bottom: ${LAYOUT2.spacing1}px;
`;

const SECTION_HEADER_STYLE = css`
  label: PHOTO_GALLERY_SECTION_HEADER_STYLE;
  ${TYPOGRAPHY2.tinyBold};
  color: var(--text);
  height: ${LAYOUT2.spacing4}px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
`;

const THUMBNAILS_CONTAINER_STYLE = css`
  label: PHOTO_GALLERY_THUMBNAILS_CONTAINER_STYLE;
  display: flex;
  flex-wrap: wrap;
  gap: ${LAYOUT2.spacing1}px;
`;

const THUMBNAIL_WRAPPER_STYLE = css`
  label: PHOTO_GALLERY_THUMBNAIL_WRAPPER_STYLE;
  width: ${THUMBNAIL_SIZE}px;
  height: ${THUMBNAIL_SIZE}px;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const THUMBNAIL_IMAGE_STYLE = css`
  label: PHOTO_GALLERY_THUMBNAIL_IMAGE_STYLE;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const SHOWN_ON_SCOPE_LABEL_CONTAINER_STYLE = css`
  label: PHOTO_GALLERY_SHOWN_ON_SCOPE_LABEL_CONTAINER_STYLE;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 24px;
  background-color: ${COLORS2.alwaysDark.background.black};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SHOWN_ON_SCOPE_LABEL_TEXT_STYLE = css`
  label: PHOTO_GALLERY_SHOWN_ON_SCOPE_LABEL_TEXT_STYLE;
  ${TYPOGRAPHY2.tinyBold}
  color: ${COLORS2.alwaysDark.text.white};
  text-transform: uppercase;
`;

const PHOTO_UPLOAD_INDICATOR_STYLE = css`
  label: PHOTO_GALLERY_UPLOAD_INDICATOR_STYLE;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  pointer-events: none;
`;

const ADD_PHOTO_TILE_STYLE = css`
  label: PHOTO_GALLERY_ADD_PHOTO_TILE_STYLE;
  width: ${THUMBNAIL_SIZE}px;
  height: ${THUMBNAIL_SIZE}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid var(--border);
  background-color: var(--background2);
  transition: transform 0.2s ease;
  gap: 4px;

  &:hover {
    transform: scale(1.05);
  }
`;

const ADD_PHOTO_TEXT_STYLE = css`
  label: PHOTO_GALLERY_ADD_PHOTO_TEXT_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  text-align: center;
  line-height: 1;
`;

const HIDDEN_FILE_INPUT_STYLE = css`
  label: PHOTO_GALLERY_HIDDEN_FILE_INPUT_STYLE;
  display: none;
`;

const REMOVE_DUPES_BUTTON_CONTAINER_STYLE = css`
  label: PHOTO_GALLERY_REMOVE_DUPES_BUTTON_CONTAINER_STYLE;
  display: flex;
  justify-content: flex-start;
  padding-top: ${LAYOUT2.spacing1}px;
`;

const ERROR_THUMBNAIL_STYLE = css`
  label: PHOTO_GALLERY_ERROR_THUMBNAIL_STYLE;
  width: ${THUMBNAIL_SIZE}px;
  height: ${THUMBNAIL_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid var(--border);
  background-color: var(--background2);
  transition: transform 0.2s ease;
  color: var(--text3);

  &:hover {
    transform: scale(1.05);
  }
`;

const PhotoThumbnail = ({
  scanId,
  photo,
  scanPartContext,
  isShownOnScope,
}: {
  scanId: string;
  photo: ScanPhoto;
  scanPartContext: ScanPartContext;
  isShownOnScope?: boolean;
}) => {
  // A square tile: a 4:3 photo's long side spans THUMBNAIL_SIZE × 4/3 once cropped to cover it.
  const uri = usePhotoUriForLayoutSize(photo.photoId, (THUMBNAIL_SIZE * 4) / 3);
  const navigateToPhoto = useNavigateToPhoto();
  const [hasError, setHasError] = useState(false);
  const visibilityRef = useRef<HTMLDivElement>(null);
  const shouldLoadImage = useLazyInView(visibilityRef);

  const handleClick = () => navigateToPhoto({ photoId: photo.photoId, groupId: scanPartContext.groupId, taskId: scanPartContext.taskId });

  if (hasError) {
    return (
      <div css={ERROR_THUMBNAIL_STYLE} onClick={handleClick}>
        <CancelIcon size={40} />
      </div>
    );
  }

  return (
    <div ref={visibilityRef} css={THUMBNAIL_WRAPPER_STYLE} onClick={handleClick}>
      {shouldLoadImage && (
        <img
          css={THUMBNAIL_IMAGE_STYLE}
          src={uri}
          alt={photo.photoId}
          onError={() => setHasError(true)}
        />
      )}
      {photo.photoSource === 'upload' && (
        <div css={PHOTO_UPLOAD_INDICATOR_STYLE}>
          <UploadIndicatorIcon size={24} />
        </div>
      )}
      {isShownOnScope && (
        <div css={SHOWN_ON_SCOPE_LABEL_CONTAINER_STYLE}>
          <div css={SHOWN_ON_SCOPE_LABEL_TEXT_STYLE}>Shown on Scope</div>
        </div>
      )}
    </div>
  );
};

const PhotoGallerySectionView = ({
  scanId,
  section,
}: {
  scanId: string;
  section: PhotoGallerySection;
}) => {
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkPhotoUploader = useBulkPhotoUploader();
  const showRemoveDuplicatesButton = !!section.onRemoveDuplicates && scanIsEditable && scanPartHasDuplicatePhotos(section.scanPart);
  const removeDuplicatesLabel = section.scanPartContext.taskId
    ? 'Remove duplicate photos on task'
    : section.scanPartContext.groupId
    ? 'Remove duplicate photos on group'
    : 'Remove duplicate photos';
  return (
    <div css={SECTION_STYLE}>
      <div css={SECTION_HEADER_STYLE}>
        {section.header} ({pluralize(section.photos.length, 'photo')})
      </div>
      <PhotoDropZone scanPart={section.onAddPhotos ? section.scanPart : null} scanPartContext={section.scanPartContext}>
        <div css={THUMBNAILS_CONTAINER_STYLE}>
          {section.photos.map((photo, index) => {
            const totalPhotos = section.photos.length;
            const shownOnScopeStartIndex = totalPhotos <= 3 ? 0 : totalPhotos - 3;
            const isTaskSection = !!section.scanPartContext.taskId;
            const isShownOnScope = isTaskSection && index >= shownOnScopeStartIndex;

            return (
              <PhotoThumbnail
                key={photo.photoId}
                scanId={scanId}
                photo={photo}
                scanPartContext={section.scanPartContext}
                isShownOnScope={isShownOnScope}
              />
            );
          })}
          {section.onAddPhotos && scanIsEditable && (
            <label
              css={ADD_PHOTO_TILE_STYLE}
              title='Add photo'
              onClick={event => {
                event.preventDefault();
                fileInputRef.current?.click();
              }}
            >
              <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                multiple
                css={HIDDEN_FILE_INPUT_STYLE}
                onClick={event => event.stopPropagation()}
                onChange={async event => {
                  await bulkPhotoUploader(event.currentTarget.files, section.scanPart, section.scanPartContext);
                  event.currentTarget.value = '';
                }}
              />
              <AddPhotoIcon color='var(--text2)' width={16} height={16} />
              <div css={ADD_PHOTO_TEXT_STYLE}>Add Photo</div>
            </label>
          )}
        </div>
      </PhotoDropZone>
      {showRemoveDuplicatesButton && (
        <div css={REMOVE_DUPES_BUTTON_CONTAINER_STYLE}>
          <Button buttonStyle='gray' size='small' onClick={section.onRemoveDuplicates}>
            {removeDuplicatesLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export const PhotoGallery = ({
  scanId,
  sections,
}: {
  scanId: string;
  sections: PhotoGallerySection[];
}) => {
  return (
    <>
      {sections.map((section, index) => (
        <PhotoGallerySectionView
          key={index}
          scanId={scanId}
          section={section}
        />
      ))}
    </>
  );
};
